import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useDownloadReport, useReport, useReportVersions } from '../../hooks/useReports'
import { useAuth } from '../../hooks/useAuth'
import { formatVolume } from '../../utils/formatInTimezone'
import type { ReportRow, ReportVersion } from '../../types/flowBilling'
import {
  COVERAGE_LABEL,
  COVERAGE_STYLE,
  coverageExplanation,
  formatSilentRanges,
  silentDayCount,
} from './coverage'

/**
 * One report, read from the stored run.
 *
 * The point of this page is that nobody has to download anything to read a
 * figure. Everything shown comes from the run as it was generated — the rows,
 * the totals, the unit, the timezone — so what an irrigator reads here is what
 * was billed, not a fresh query that has since absorbed late data.
 *
 * Sorting and filtering act on the loaded rows only. They change what is on
 * screen and never what a download contains: a download is always the whole
 * report, which is what makes it safe to hand to an accountant.
 */

type SortKey = 'ric' | 'port' | 'siteName' | 'totalVolume' | 'readings'

/**
 * One step of the period stepper.
 *
 * Rendered as an inert chevron at either end of the history rather than
 * removed: a control that vanishes moves the ones beside it, and the reader has
 * to find them again on every month.
 */
function StepLink({
  to,
  label,
  endLabel,
  direction,
}: {
  to: number | null
  label: string
  endLabel: string
  direction: 'prev' | 'next'
}) {
  const Icon = direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon
  const base = 'flex h-8 w-8 items-center justify-center'
  if (to == null) {
    return (
      <span className={`${base} text-gray-300 cursor-not-allowed`} title={endLabel} aria-hidden="true">
        <Icon className="h-5 w-5" />
      </span>
    )
  }
  return (
    <Link
      to={`/reports/${to}`}
      aria-label={label}
      title={label}
      className={`${base} text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${
        direction === 'prev' ? 'rounded-l-lg' : 'rounded-r-lg'
      }`}
    >
      <Icon className="h-5 w-5" />
    </Link>
  )
}

/** "2026-09-01 00:00:00" → "Sep 2026": the stepper's label, short enough to sit between two chevrons. */
function shortMonth(startLocal: string): string {
  const [year, month] = startLocal.slice(0, 7).split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** One line of the version picker: when, how much, and how it was issued. */
function describeVersion(version: ReportVersion, unit: string): string {
  const issued = version.sentAt ? 'emailed' : version.status === 'superseded' ? 'replaced' : 'current'
  const devices = `${version.deviceCount} device${version.deviceCount === 1 ? '' : 's'}`
  const volume = `${formatVolume(version.totalVolume, version.decimalPlaces)} ${unit}`
  return `${version.generatedAt || 'not generated'} · ${devices} · ${volume} · ${issued}`
}

export default function ReportViewPage() {
  const { runId } = useParams<{ runId: string }>()
  const id = runId ? Number(runId) : undefined
  const { data: report, isLoading, isError, error } = useReport(id)
  const { data: versionList } = useReportVersions(id)
  const versions = versionList ?? []
  const navigate = useNavigate()
  const download = useDownloadReport()
  const { isAdmin } = useAuth()
  // An administrator sees the page as the group's own users do until they ask
  // for the diagnostics: what the district reads is what is being checked.
  // State, not a URL param, so it carries across the month steppers. The
  // server still sends the admin payload; this hides exactly what it strips
  // for everyone else (Readings) plus the admin-only diagnostics.
  const [viewAsCustomer, setViewAsCustomer] = useState(true)
  const showReadings = isAdmin() && !viewAsCustomer

  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('ric')
  const [sortAsc, setSortAsc] = useState(true)

  const rows = useMemo<ReportRow[]>(() => {
    if (!report) return []
    const needle = search.trim().toLowerCase()
    const filtered = needle
      ? report.rows.filter((r) =>
          [r.ric, r.siteName, r.port, r.sigfoxId]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(needle))
        )
      : report.rows

    return [...filtered].sort((a, b) => {
      const left = a[sortKey]
      const right = b[sortKey]
      const compared =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left ?? '').localeCompare(String(right ?? ''))
      return sortAsc ? compared : -compared
    })
  }, [report, search, sortKey, sortAsc])

  // Arrow keys step through the months, as they would through photos. Ignored
  // while typing in the search box or using the version picker.
  const prevId = report?.prevId ?? null
  const nextId = report?.nextId ?? null
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      const to = e.key === 'ArrowLeft' ? prevId : e.key === 'ArrowRight' ? nextId : null
      if (to != null) navigate(`/reports/${to}`)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [prevId, nextId, navigate])

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((prev) => !prev)
    else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading report…</div>
  }

  if (isError || !report) {
    const status = (error as any)?.response?.status
    return (
      <div className="p-6 max-w-3xl">
        <Link to="/reports/flowmeter" className="text-sm text-blue-600 hover:underline">
          ‹ Back to reports
        </Link>
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">
            {status === 404 ? 'Report not found' : 'Could not load this report'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {status === 404
              ? 'It may have been removed, or it belongs to a group you do not have access to.'
              : (error as any)?.response?.data?.error?.message || 'Please try again.'}
          </p>
        </div>
      </div>
    )
  }

  const reconfigured = report.rows.filter((r) => r.reconfiguredInPeriod)
  // Admin-only: rows whose total leaves days out. Coverage is per device, so a
  // two-port device would otherwise be named twice.
  const incomplete = report.rows.filter((r) => (r.silentRanges?.length ?? 0) > 0)
  const incompleteDevices = Array.from(new Map(incomplete.map((r) => [r.sigfoxId, r])).values())

  // Mirrors the server's pattern, and is only ever used if the browser cannot
  // read Content-Disposition. "report-5.csv" told a district nothing.
  const fileStem = `${report.period.startLocal.slice(0, 7).replace('-', '-')}-${report.groupName
    .trim()
    .replace(/\s+/g, '.')}-report`

  const header = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <th
      className={`px-3 py-2 text-${align} text-xs font-medium text-gray-600 uppercase cursor-pointer select-none hover:text-gray-900`}
      onClick={() => toggleSort(key)}
    >
      {label}
      {sortKey === key && <span className="ml-1">{sortAsc ? '▲' : '▼'}</span>}
    </th>
  )

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          {/* A season is read month by month. Sending the reader back to the
              list between every one of them is the long way round, so the
              month steps in place and the list stays one click away. */}
          <nav aria-label="Report navigation" className="flex items-center gap-3 text-sm">
            <Link
              to="/reports/flowmeter"
              className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              All reports
            </Link>
            <span className="h-5 w-px bg-gray-300" aria-hidden="true" />
            <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white shadow-sm">
              <StepLink to={report.prevId} direction="prev" label="Previous month" endLabel="No earlier report" />
              <span className="min-w-[6.5rem] px-2 text-center font-medium text-gray-900 tabular-nums">
                {shortMonth(report.period.startLocal)}
              </span>
              <StepLink to={report.nextId} direction="next" label="Next month" endLabel="No later report" />
            </div>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 mt-4">
            {report.groupName} — {report.period.label}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {report.period.startLocal} – {report.period.endLocal} ({report.period.timezone})
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin() && (
            <div
              role="group"
              aria-label="Preview the page as"
              className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-sm mr-2"
            >
              {[
                { customer: false, label: 'Admin view' },
                { customer: true, label: `As ${report.groupName}` },
              ].map(({ customer, label }) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={viewAsCustomer === customer}
                  className={`rounded-md px-3 py-1.5 ${
                    viewAsCustomer === customer ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setViewAsCustomer(customer)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={download.isPending}
            onClick={() => download.mutate({ id: report.id, format: 'pdf', fallback: fileStem })}
          >
            Download PDF
          </button>
          {/* CSV is the default: it is what the district feeds into its own
              billing, so it takes the primary button. */}
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            disabled={download.isPending}
            onClick={() => download.mutate({ id: report.id, format: 'csv', fallback: fileStem })}
          >
            Download CSV
          </button>
        </div>
      </div>

      {/* A month that was re-run has several versions, and the banner this
          replaces could name exactly one of them. Somebody reconciling a
          corrected month wants to see them all, with enough on each line to
          tell why they differ — a device joined the group, a sensor was
          reconfigured. Nothing renders when a period has only ever had one. */}
      {versions.length > 1 && (
        <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-4 text-sm">
          <label className="block text-gray-700 mb-1" htmlFor="report-version">
            This period has {versions.length} versions
          </label>
          <select
            id="report-version"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white max-w-full"
            value={report.id}
            onChange={(e) => navigate(`/reports/${e.target.value}`)}
          >
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {describeVersion(version, report.unit)}
              </option>
            ))}
          </select>
          {/* Why this version exists belongs on the page, not inside a menu
              somebody has to open to find it. */}
          {report.revision?.reason && (
            <div className="text-gray-600 mt-2">Reason: {report.revision.reason}</div>
          )}
        </div>
      )}

      {report.status === 'superseded' && (
        <div className="mb-4 border-l-4 border-gray-400 bg-gray-50 p-4 text-sm text-gray-700">
          This report has been superseded by a later revision. It is kept because it is the copy
          that was originally sent.
        </div>
      )}

      {/* An empty month is still a report: the file the district received says
          why, and so does the page, rather than an empty table. */}
      {report.notice && (
        <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-4 text-sm font-medium text-gray-900">
          {report.notice}
        </div>
      )}

      <div className="mb-3">
        <input
          type="search"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72"
          placeholder="Search RIC or site"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {header('ric', 'RIC')}
                {header('port', 'Port')}
                {header('siteName', 'Site')}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                  Product
                </th>
                {header('totalVolume', `Volume (${report.unit})`, 'right')}
                {showReadings && header('readings', 'Readings', 'right')}
                {showReadings && (
                  <th
                    className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase"
                    title="Messages the device sent that never arrived (gaps in its sequence numbers)"
                  >
                    Lost messages
                  </th>
                )}
                {showReadings && (
                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase"
                    title="Days the device reported, and why any are missing from the total"
                  >
                    Coverage
                  </th>
                )}
                {/* A diagnostic, like the columns before it: the district bills
                    on the volume, and a timestamp invites questions the
                    Coverage column answers better. */}
                {showReadings && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                    Last reading
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row, index) => (
                <tr key={`${row.sigfoxId}-${row.port}-${index}`} className="hover:bg-blue-50">
                  <td className="px-3 py-2 text-sm text-gray-900">{row.ric}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.port}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.siteName}</td>
                  <td className="px-3 py-2 text-sm text-gray-500">{row.sensorProduct}</td>
                  <td className="px-3 py-2 text-sm text-gray-900 text-right tabular-nums">
                    {formatVolume(row.totalVolume, report.decimalPlaces)}
                  </td>
                  {showReadings && (
                    <td className="px-3 py-2 text-sm text-gray-700 text-right tabular-nums">
                      {row.readings ?? ''}
                    </td>
                  )}
                  {showReadings && (
                    <td className="px-3 py-2 text-sm text-gray-700 text-right tabular-nums">
                      {/* Exact, and never coloured: gaps in the device's Sigfox
                          sequence number, across every message type it sends.
                          A silent device leaves no gap, so a 0 here says nothing
                          about silence — that is the Coverage column's job. */}
                      {row.missedMessages ?? ''}
                    </td>
                  )}
                  {showReadings && (
                    <td className="px-3 py-2 text-sm cursor-help" title={row.coverageStatus ? coverageExplanation(row) : undefined}>
                      {row.coverageStatus && (
                        <>
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                              COVERAGE_STYLE[row.coverageStatus]
                            }`}
                          >
                            {COVERAGE_LABEL[row.coverageStatus]}
                          </span>
                          {(row.silentRanges?.length ?? 0) > 0 && (
                            <div className="mt-0.5 text-xs text-gray-600 whitespace-nowrap">
                              {silentDayCount(row.silentRanges!)} day
                              {silentDayCount(row.silentRanges!) === 1 ? '' : 's'} not in total
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  )}
                  {showReadings && (
                    <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {/* Blank, not a dash: a gap between this and the period end
                          is how an outage shows up, and a placeholder hides it. */}
                      {row.lastReading || ''}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reconfigured.length > 0 && (
        <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
          {reconfigured.length} flow sensor{reconfigured.length === 1 ? ' was' : 's were'}{' '}
          reconfigured inside the period (
          {reconfigured.map((r) => `${r.ric} / ${r.port}`).join(', ')}). Each total spans both
          configurations.
        </div>
      )}

      {/* One summary instead of a red number per row: what is missing from
          which totals, and what to do about it. Never estimated here — whether
          to bill on an estimate is the district's call. */}
      {showReadings && incompleteDevices.length > 0 && (
        <div className="mt-4 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-900">
          <p className="font-medium">
            {incomplete.length} of {report.rows.length} sensors: totals exclude some days.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {incompleteDevices.map((r) => (
              <li key={r.sigfoxId}>
                {r.ric}: no readings {formatSilentRanges(r.silentRanges ?? [])}
                {(r.silentRanges ?? []).every((range) => range.commissioning) && ' (beside a configuration change)'}
              </li>
            ))}
          </ul>
          <p className="mt-1">
            Any flow on those days is not in the total and has not been estimated. Check against the meter
            register before billing.
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-3">
        Volumes are shown in the unit this report was generated in and are not recomputed. Generated{' '}
        {report.generatedAt} ({report.period.timezone}).
      </p>
    </div>
  )
}
